<template>
    <PageHeader :title="t('my.rechargeRecord')" />
    <div class="page-wrapper" style="padding-top: calc(50px * 1.1)!important;">
        <div class="recharge-record-container desktop-container">
            <!-- <div class="tab-navigation">
                <div class="tab-item" :class="{ 'active': activeTab === 'offline' }" @click="switchTab('offline')">{{ t('rechargeRecord.offline') }}</div>
                <div class="tab-item" :class="{ 'active': activeTab === 'online' }" @click="switchTab('online')">{{ t('rechargeRecord.online') }}</div>
            </div> -->

            <el-scrollbar
                ref="scrollbarRef"
                class="record-list"
                v-loading="loading"
                :element-loading-text="t('rechargeRecord.loading')"
                @scroll="handleScroll"
            >
                <div v-if="!loading && records.length === 0" class="empty-state">
                    <div class="empty-icon">📋</div>
                    <div class="empty-text">{{ t('rechargeRecord.noRecords') }}</div>
                </div>

                <div v-for="record in records" :key="record.id" class="record-item">
                    <div class="record-header">
                        <div class="record-id"><span class="label">{{ t('rechargeRecord.orderNo') }}</span><span class="value">{{ record.orderNo }}</span></div>
                    </div>
                    <div class="record-details">
                        <div class="detail-row"><span class="label">{{ t('rechargeRecord.rechargeAmount') }}</span><span class="value">{{ appConfig.currency.symbol }}{{ record.amount }}</span></div>
                        <div class="detail-row"><span class="label">{{ t('rechargeRecord.rechargeStatus') }}</span><span class="value status" :class="'status-' + record.status">{{ record.statusText }}</span></div>
                    </div>
                </div>

                <div v-if="!loading && records.length > 0 && noMore" class="no-more-section">
                    <div class="no-more-text">{{ t('rechargeRecord.noMore') }}</div>
                </div>
            </el-scrollbar>
            <el-backtop :bottom="30" target=".record-list .el-scrollbar__wrap" />
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { getRechargeLog } from '@/api'
import { appConfig } from '@/config/app'
import PageHeader from '@/components/PageHeader.vue'

const router = useRouter()
const { t } = useI18n()

const activeTab = ref('offline')
const loading = ref(false)
const noMore = ref(false)
const currentPage = ref(1)
const pageSize = 10
const scrollbarRef = ref(null)
const records = ref([])

const statusMap = computed(() => (status) => {
  if (status > 3) return t('rechargeRecord.statusEmpty')
  const map = {
    1: t('rechargeRecord.statusPending'),
    2: t('rechargeRecord.statusApproved'),
    3: t('rechargeRecord.statusFailed')
  }
  return map[status] || t('rechargeRecord.statusEmpty')
})

const fetchRecords = async (page = 1, isLoadMore = false) => {
  if (!isLoadMore) {
    records.value = []
    currentPage.value = 1
    noMore.value = false
  }

  loading.value = true

  try {
    const response = await getRechargeLog({ page, limit: pageSize })
    if (response.data.code === 200) {
      const processedData = response.data.data.list.map(item => ({
        id: item.id,
        orderNo: item.id,
        amount: item.num,
        status: item.status,
        statusText: statusMap.value(item.status),
        addTime: item.addtime,
        endTime: item.endtime
      }))

      if (isLoadMore) {
        records.value.push(...processedData)
        currentPage.value = page
      } else {
        records.value = processedData
      }

      noMore.value = response.data.data.list.length < pageSize
      currentPage.value = response.data.data.page
    }
  } catch (error) {
    console.error('获取充值记录失败:', error)
  } finally {
    loading.value = false
  }
}

const handleScroll = ({ scrollTop }) => {
  const wrap = scrollbarRef.value?.wrapRef
  if (!wrap) return

  const { scrollHeight, clientHeight } = wrap
  const distance = scrollHeight - scrollTop - clientHeight

  if (distance < 50 && !loading.value && !noMore.value) {
    console.log('下拉到第', currentPage.value + 1, '页')
    loadMore()
  }
}

const loadMore = () => {
  fetchRecords(currentPage.value + 1, true)
}

const switchTab = (tab) => {
  if (activeTab.value === tab) return
  activeTab.value = tab
  currentPage.value = 1
  noMore.value = false
  records.value = []
  fetchRecords(1, false)
  scrollbarRef.value?.setScrollTop(0)
}

onMounted(() => {
  fetchRecords(1, false)
})
</script>

<style scoped>
.recharge-record-container {
    background: var(--bg-secondary);
    height: calc(100vh - 54px); /* 用 height，不要 min-height */
    overflow: hidden;          /* ⭐关键 */
    padding: 10px
}

.tab-navigation {
    display: flex;
    background: var(--card-bg);
    border-radius: 10px;
    padding: 5px;
    margin-bottom: 10px;
    box-shadow: var(--shadow-base)
}

.tab-navigation .tab-item {
    flex:1;
    text-align: center;
    padding: 10px 15px;
    border-radius: 8px;
    font-size: 14px;
    color: var(--text-secondary);
    transition: all .3s ease;
    cursor: pointer
}

.tab-navigation .tab-item.active {
    background: linear-gradient(135deg,#667eea,#764ba2);
    color: #fff;
    box-shadow: 0 3px 10px #667eea4d;
    transform: translateY(-1px)
}

.tab-navigation .tab-item:hover:not(.active) {
    background: #667eea1a;
    color: #667eea
}

.record-list {
    height: calc(100vh - 54px);
    position: relative
}

.record-list .record-item {
    background: var(--card-bg);
    border-radius: 12px;
    margin-bottom: 10px;
    padding: 15px;
    box-shadow: var(--shadow-base);
    transition: all .3s ease
}

.record-list .record-item:hover {
    box-shadow: 0 4px 15px #0000001f;
    transform: translateY(-2px)
}

.record-list .record-item .record-header {
    border-bottom: 1px solid var(--border-color);
    padding-bottom: 10px;
    margin-bottom: 10px;
    display: flex;
    justify-content: space-between;
    align-items: center
}

.record-list .record-item .record-header .record-id {
    display: flex;
    align-items: center;
    gap: 8px
}

.record-list .record-item .record-header .record-time {
    display: flex;
    align-items: center;
    gap: 8px
}

.record-list .record-item .record-header .record-id .label,
.record-list .record-item .record-header .record-time .label {
    font-size: 13px;
    color: var(--text-secondary);
    font-weight: 500
}

.record-list .record-item .record-header .record-id .value,
.record-list .record-item .record-header .record-time .value {
    font-size: 14px;
    color: var(--text-primary);
    font-weight: 600;
    font-family: Courier New,monospace
}

.record-list .record-item .record-details .detail-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px
}

.record-list .record-item .record-details .detail-row:last-child {
    margin-bottom: 0
}

.record-list .record-item .record-details .detail-row .label {
    font-size: 14px;
    color: var(--text-secondary);
    font-weight: 500
}

.record-list .record-item .record-details .detail-row .value {
    font-size: 14px;
    color: var(--text-primary);
    font-weight: 600
}

.record-list .record-item .record-details .detail-row .value.status {
    padding: 4px 8px;
    border-radius: 10px;
    font-size: 12px
}

.record-list .record-item .record-details .detail-row .value.status.status-1 {
    background: #ffc1071a;
    color: #ff6b35
}

.record-list .record-item .record-details .detail-row .value.status.status-2 {
    background: #28a7451a;
    color: #28a745
}

.record-list .record-item .record-details .detail-row .value.status.status-3 {
    background: #6c757d1a;
    color: #6c757d
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 30px
}

.empty-state .empty-icon {
    font-size: 60px;
    margin-bottom: 15px;
    opacity: .6
}

.empty-state .empty-text {
    font-size: 14px;
    color: var(--text-secondary);
    text-align: center;
    line-height: 1.6
}

.no-more-section {
    padding: 16px;
    text-align: center;
    color: #999
}

.no-more-section .no-more-text {
    font-size: 12px
}

:deep(.el-scrollbar__bar) {
    display: none
}

.el-loading-mask {
    display: flex !important
}

@media screen and (min-width: 768px) {
    .recharge-record-container {
        padding:15px
    }

    .tab-navigation {
        margin: 15px 0
    }

    .tab-navigation .tab-item {
        padding: 12px 20px;
        font-size: 15px
    }

    .record-list .record-item {
        padding: 20px
    }

    .record-list .record-item .record-header {
        padding-bottom: 12px;
        margin-bottom: 12px
    }

    .record-list .record-item .record-details .detail-row {
        margin-bottom: 10px
    }
}
</style>
